import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Spinner,
  Textarea,
} from "@magarac/ui";
import { formatCents } from "@magarac/shared/money";
import { quoteSchema, type Quote } from "@magarac/shared/schemas/documents";
import { z } from "zod";
import { HelpIcon } from "../../../components/HelpIcon";
import {
  LineItemsEditor,
  draftsToLines,
  toDrafts,
  type LineDraft,
} from "../../../components/LineItemsEditor";
import { api } from "../../../lib/api";
import {
  QUOTE_STATUS_LABELS,
  dayText,
  dollarsText,
  downloadPdf,
  parseDollars,
  statusTone,
} from "../../../lib/documents";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/quotes/$id")({
  component: QuotePage,
});

const responseSchema = z.object({ quote: quoteSchema });

/** CRM-17, CRM-18: one quote: edit, send, follow what the customer did. Screen key: quotes.edit */
function QuotePage() {
  const { id } = Route.useParams();
  const { active } = useTenant();
  const q = useQuery({
    queryKey: ["quote", active?.id, id],
    queryFn: () => api.get(`/quotes/${id}`, responseSchema),
    enabled: Boolean(active),
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <Banner tone="danger">{messageFor(q.error)}</Banner>;
  return <QuoteView key={q.data.quote.id} quote={q.data.quote} />;
}

function QuoteView({ quote }: { quote: Quote }) {
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const invoicing = useSetting("invoicing").value;
  const editable = quote.status !== "approved" && quote.status !== "declined";
  const [drafts, setDrafts] = useState<LineDraft[]>(() => toDrafts(quote.lines));
  const [deposit, setDeposit] = useState(quote.depositCents ? dollarsText(quote.depositCents) : "");
  const [notes, setNotes] = useState(quote.notes ?? "");
  const [expiresOn, setExpiresOn] = useState(quote.expiresOn ?? "");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const refresh = (data: { quote: Quote }) => {
    queryClient.setQueryData(["quote", active?.id, quote.id], data);
    void queryClient.invalidateQueries({ queryKey: ["quotes"] });
  };
  const save = useMutation({
    mutationFn: (body: unknown) => api.patch(`/quotes/${quote.id}`, body, responseSchema),
    onSuccess: (d) => {
      setNotice("Saved.");
      refresh(d);
    },
    onError: (err) => setError(messageFor(err)),
  });
  const send = useMutation({
    mutationFn: () => api.post(`/quotes/${quote.id}/send`, {}, responseSchema),
    onSuccess: (d) => {
      setNotice("Sent. The customer gets a text (or an email) with the link.");
      refresh(d);
    },
    onError: (err) => setError(messageFor(err)),
  });
  const decline = useMutation({
    mutationFn: () =>
      api.post(`/quotes/${quote.id}/decline`, { reason: reason.trim() || null }, responseSchema),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  });

  const changes = () => {
    const parsed = draftsToLines(drafts);
    if ("error" in parsed) return { error: parsed.error };
    const depositCents = deposit.trim() ? parseDollars(deposit) : 0;
    if (depositCents === null || depositCents < 0)
      return { error: "The deposit must be a dollar amount (or empty for none)." };
    return {
      body: {
        lines: parsed.lines,
        depositCents,
        notes: notes.trim() || null,
        expiresOn: expiresOn || null,
        expectedUpdatedAt: quote.updatedAt,
      },
    };
  };
  const doSave = async (thenSend: boolean) => {
    setError(null);
    setNotice(null);
    const c = changes();
    if ("error" in c) return setError(c.error ?? "Check the lines.");
    const ok = await save
      .mutateAsync(c.body)
      .then(() => true)
      .catch(() => false);
    if (ok && thenSend) send.mutate();
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={`Quote #${quote.number}`} help={<HelpIcon screen="quotes.edit" />} />
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={statusTone(quote.status)}>{QUOTE_STATUS_LABELS[quote.status]}</Badge>
        <Link
          to="/customers/$id"
          params={{ id: quote.customerId }}
          className="text-brand-700 underline"
        >
          {quote.customerName}
        </Link>
        {quote.jobId ? (
          <Link to="/jobs/$id" params={{ id: quote.jobId }} className="text-brand-700 underline">
            Job #{quote.jobNumber}
          </Link>
        ) : null}
        {quote.expiresOn && editable ? (
          <span className="text-muted">Good until {dayText(quote.expiresOn)}</span>
        ) : null}
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}
      {quote.status === "approved" ? (
        <Banner tone="ok">
          Approved {dayText(quote.approvedAt)}
          {quote.approvedName ? ` by ${quote.approvedName}` : ""}.
          {quote.depositCents > 0
            ? ` A ${formatCents(quote.depositCents)} deposit was requested.`
            : ""}
        </Banner>
      ) : null}
      {quote.status === "declined" ? (
        <Banner tone="warn">
          Declined {dayText(quote.declinedAt)}
          {quote.declineReason ? `: "${quote.declineReason}"` : "."} Make a new quote to offer
          something else.
        </Banner>
      ) : null}
      {quote.status === "expired" ? (
        <Banner tone="warn">
          This quote passed its date. Change the date or the prices if needed, then send it again.
        </Banner>
      ) : null}

      <Card className="mb-4">
        <LineItemsEditor
          drafts={drafts}
          onChange={setDrafts}
          taxRateBp={editable ? (invoicing?.taxRateBp ?? quote.taxRateBp) : quote.taxRateBp}
          taxLabel={invoicing?.taxLabel ?? quote.taxLabel}
          disabled={!editable}
        />
      </Card>
      <Card className="mb-4">
        <Field
          id="q-deposit"
          label="Deposit to approve"
          hint="Paid by card right after approving. Empty for none."
        >
          <Input
            id="q-deposit"
            inputMode="decimal"
            value={deposit}
            disabled={!editable}
            onChange={(e) => setDeposit(e.target.value)}
          />
        </Field>
        <Field
          id="q-expires"
          label="Good until"
          hint="Set when you send it, from Settings > Invoicing."
        >
          <Input
            id="q-expires"
            type="date"
            value={expiresOn}
            disabled={!editable}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
        </Field>
        <Field id="q-notes" label="Note to the customer">
          <Textarea
            id="q-notes"
            rows={3}
            maxLength={2000}
            value={notes}
            disabled={!editable}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </Card>

      <div className="flex flex-wrap gap-2">
        {editable ? (
          <>
            <Button busy={save.isPending || send.isPending} onClick={() => void doSave(true)}>
              {quote.sentAt ? "Save and send again" : "Save and send"}
            </Button>
            <Button variant="secondary" busy={save.isPending} onClick={() => void doSave(false)}>
              Save
            </Button>
          </>
        ) : null}
        <Button
          variant="secondary"
          onClick={() =>
            void downloadPdf(`/quotes/${quote.id}/pdf`, `quote-${quote.number}.pdf`).catch(
              (e: unknown) => setError(messageFor(e)),
            )
          }
        >
          Download PDF
        </Button>
        {quote.sentAt ? (
          <Button
            variant="ghost"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(quote.url)
                .then(() => setNotice("Customer link copied."))
            }
          >
            Copy customer link
          </Button>
        ) : null}
        {editable && quote.sentAt && !declining ? (
          <Button variant="ghost" onClick={() => setDeclining(true)}>
            Customer said no
          </Button>
        ) : null}
      </div>
      {declining ? (
        <Card className="mt-4" title="Mark declined">
          <Field id="q-reason" label="Why (optional)">
            <Input
              id="q-reason"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <div className="flex gap-2">
            <Button variant="danger" busy={decline.isPending} onClick={() => decline.mutate()}>
              Mark declined
            </Button>
            <Button variant="secondary" onClick={() => setDeclining(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}
      {quote.status === "approved" && quote.jobId ? (
        <p className="mt-4 text-sm">
          Next: open{" "}
          <Link to="/jobs/$id" params={{ id: quote.jobId }} className="text-brand-700 underline">
            job #{quote.jobNumber}
          </Link>{" "}
          to schedule it, and create the invoice when the work is done.
        </p>
      ) : null}
    </div>
  );
}
